import type { AppState } from '@/store/rootTypes'
import type { StudentWorkspace } from '@/entities/student-workspace'
import type { ExerciseAttempt } from '@/entities/attempt'
import type { LearningReport } from '@/entities/report'
import type { GeneratedLearningPlan, WrongQuestion, WrongQuestionMasteryState, WrongQuestionWorkflowState } from '@/types'
import { activeFieldsFromWorkspace, createEmptyWorkspace } from '@/store/workspace'
import { createInitialGameState } from '@/engine/gameEngine'
import { createEmptyArchiveStats } from '@/store/retention'
import { LocalKeyValueRepository, type KeyValueRepository } from '@/repositories/key-value'
import { migrateStudentLearningProfile } from '@/entities/profile/migration'
import { getPreferredLearnerType } from '@/entities/profile/getSocialLearningProfile'
import { getStarterOutfit } from '@/data/outfitConfig'
import { createLearningStage } from '@/entities/stage'
import type { GraduationAssessmentResponse, GraduationAssessmentSession } from '@/entities/graduation-assessment'
import type { LearningLayer } from '@/types'
import { createLearningTaskCompletionKey } from '@/entities/progress'
import {
  createFormativeQuestionEvidenceSnapshot,
  createFormativeQuestionInstanceId,
} from '@/entities/formative-task-session'
import { normalizeAITeacherSession } from '@/entities/ai-teacher'

export const LEGACY_LEARNING_STORAGE_KEY = 'openelp-learning-plan-v1'

export interface LegacyLearningState {
  currentPlan?: GeneratedLearningPlan | null
  currentPlanKey?: string | null
  attempts?: ExerciseAttempt[]
  currentReport?: LearningReport | null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function normalizeWrongQuestion(question: WrongQuestion): WrongQuestion {
  const legacyStatus = question.status
  const masteryState: WrongQuestionMasteryState = question.masteryState
    ?? (legacyStatus === 'mastered' ? 'mastered'
      : legacyStatus === 'corrected' ? 'corrected'
      : legacyStatus === 'awaiting-certification' ? 'awaiting-certification'
      : 'wrong')
  const workflowState: WrongQuestionWorkflowState = question.workflowState
    ?? (legacyStatus === 'explaining' || legacyStatus === 'reviewing' ? 'explaining'
      : legacyStatus === 'training' ? 'training'
      : legacyStatus === 'certifying' ? 'certifying'
      : 'idle')
  return { ...question, masteryState, workflowState, status: masteryState }
}

function normalizeWrongQuestions(value: unknown): WrongQuestion[] {
  return Array.isArray(value) ? value.map((question) => normalizeWrongQuestion(question as WrongQuestion)) : []
}

/**
 * Persisted state can have the current version while still missing fields
 * written by an interrupted or hand-edited browser save. Normalize those
 * fields during every Zustand rehydrate, not only version migrations.
 */
export function normalizePersistedState(persistedState: unknown): Partial<AppState> {
  if (!isRecord(persistedState)) return {}
  const state = persistedState as Partial<AppState>
  const rawWorkspaces = isRecord(state.workspaces) ? state.workspaces : null
  const workspaces = rawWorkspaces
    ? Object.fromEntries(Object.entries(rawWorkspaces).map(([studentId, value]) => {
      const workspace = isRecord(value) ? value : undefined
      return [studentId, {
        ...workspace,
        formativeTaskSessions: Array.isArray(workspace?.formativeTaskSessions) ? workspace.formativeTaskSessions : [],
        wrongQuestions: normalizeWrongQuestions(workspace?.wrongQuestions),
        rewardClaims: Array.isArray(workspace?.rewardClaims) ? workspace.rewardClaims : [],
        aiTeacherSession: workspace?.aiTeacherSession
          ? normalizeAITeacherSession(workspace.aiTeacherSession as AppState['aiTeacherSession'] & {})
          : null,
      }]
    })) as AppState['workspaces']
    : undefined
  const activeWorkspace = state.activeStudentId && workspaces
    ? workspaces[state.activeStudentId]
    : undefined
  const rawAITeacherSession = state.aiTeacherSession ?? activeWorkspace?.aiTeacherSession ?? null
  const aiTeacherSession = rawAITeacherSession
    ? normalizeAITeacherSession(rawAITeacherSession)
    : null

  return {
    ...state,
    ...(workspaces ? { workspaces } : {}),
    formativeTaskSessions: (Array.isArray(state.formativeTaskSessions)
      ? state.formativeTaskSessions
      : activeWorkspace?.formativeTaskSessions ?? []) as AppState['formativeTaskSessions'],
    rewardClaims: (Array.isArray(state.rewardClaims)
      ? state.rewardClaims
      : activeWorkspace?.rewardClaims ?? []) as AppState['rewardClaims'],
    wrongQuestions: normalizeWrongQuestions(
      Array.isArray(state.wrongQuestions) ? state.wrongQuestions : activeWorkspace?.wrongQuestions,
    ),
    aiTeacherSession,
    aiTeacherResult: aiTeacherSession?.result ?? state.aiTeacherResult ?? null,
    aiTeacherFingerprint: aiTeacherSession?.wrongItemFingerprint ?? state.aiTeacherFingerprint ?? '',
  }
}

export function mergePersistedState(persistedState: unknown, currentState: AppState): AppState {
  return { ...currentState, ...normalizePersistedState(persistedState) }
}

function scopeCurrentStageTaskProgress(workspace: StudentWorkspace): StudentWorkspace {
  const stageId = workspace.currentStage?.id
  const unitId = workspace.currentStage?.unitId ?? workspace.currentPlan?.currentUnitId
  if (!stageId || !unitId) return workspace

  const prefix = `plan:${unitId}:task:`
  const completedTasks = { ...workspace.completedTasks }
  const taskCompletedAt = { ...workspace.taskCompletedAt }
  for (const [taskId, completed] of Object.entries(workspace.completedTasks)) {
    if (!completed || !taskId.startsWith(prefix)) continue
    const completionKey = createLearningTaskCompletionKey(stageId, taskId)
    completedTasks[completionKey] = true
    if (workspace.taskCompletedAt[taskId]) {
      taskCompletedAt[completionKey] ??= workspace.taskCompletedAt[taskId]
    }
    delete completedTasks[taskId]
    delete taskCompletedAt[taskId]
  }

  return { ...workspace, completedTasks, taskCompletedAt }
}

function migrateStageScopedTaskProgress(state: AppState): AppState {
  const workspaces = Object.fromEntries(Object.entries(state.workspaces).map(([studentId, workspace]) => (
    [studentId, scopeCurrentStageTaskProgress(workspace)]
  )))
  const activeWorkspace = state.activeStudentId ? workspaces[state.activeStudentId] : undefined
  return {
    ...state,
    workspaces,
    ...(activeWorkspace ? activeFieldsFromWorkspace(activeWorkspace) : {}),
  }
}

function backfillWorkspaceFormativeQuestionEvidence(workspace: StudentWorkspace): StudentWorkspace {
  const snapshotsByExerciseId = new Map<string, string>()
  for (const session of workspace.formativeTaskSessions) {
    for (const questionId of session.questionIds) {
      const snapshot = createFormativeQuestionEvidenceSnapshot(
        session.questionSnapshots?.[questionId],
      )
      if (!snapshot) continue
      snapshotsByExerciseId.set(createFormativeQuestionInstanceId({
        studentId: session.studentId,
        stageId: session.stageId,
        taskId: session.taskId,
        contentVersion: session.contentVersion,
        questionId,
      }), snapshot)
    }
  }
  if (snapshotsByExerciseId.size === 0) return workspace

  const attempts = workspace.attempts.map((attempt) => {
    if (attempt.questionSnapshot) return attempt
    const questionSnapshot = snapshotsByExerciseId.get(attempt.exerciseId)
    return questionSnapshot ? { ...attempt, questionSnapshot } : attempt
  })
  const wrongQuestions = workspace.wrongQuestions.map((question) => {
    if (question.questionSnapshot) return question
    const questionSnapshot = snapshotsByExerciseId.get(question.originalQuestionId)
    return questionSnapshot ? { ...question, questionSnapshot } : question
  })

  return { ...workspace, attempts, wrongQuestions }
}

function migrateFormativeQuestionEvidence(state: AppState): AppState {
  const workspaces = Object.fromEntries(Object.entries(state.workspaces).map(([studentId, workspace]) => (
    [studentId, backfillWorkspaceFormativeQuestionEvidence(workspace)]
  )))
  const activeWorkspace = state.activeStudentId ? workspaces[state.activeStudentId] : undefined
  return {
    ...state,
    workspaces,
    ...(activeWorkspace ? activeFieldsFromWorkspace(activeWorkspace) : {}),
  }
}

function legacyQuestionId(exerciseId: string): string {
  return exerciseId.split('@')[0]
}

function legacyResponses(attempts: ExerciseAttempt[]): GraduationAssessmentResponse[] {
  return attempts.map((attempt) => ({
    questionId: legacyQuestionId(attempt.exerciseId),
    category: attempt.category ?? attempt.subCategory ?? '未分类',
    attempt: {
      ...attempt,
      retryCount: Math.max(0, attempt.retryCount),
    },
  }))
}

function migrateLegacyAssessmentSessions(
  workspace: StudentWorkspace,
  studentId: string,
): GraduationAssessmentSession[] {
  const existing = workspace.assessmentSessions ?? []
  const knownIds = new Set(existing.map((session) => session.id))
  const migrated = [...existing]
  const defaultLayer = workspace.currentStage?.layer
    ?? workspace.profile?.recommendedLevel
    ?? workspace.pretestLayer
    ?? workspace.student.layer
    ?? 'C'

  for (const record of workspace.assessmentRecords) {
    if (record.kind === 'certification' || !record.sessionId || knownIds.has(record.sessionId)) continue
    const attempts = workspace.attempts.filter((attempt) => attempt.assessmentSessionId === record.sessionId)
    const responses = legacyResponses(attempts)
    const manifest = [...new Set([
      ...record.correctIds,
      ...record.wrongIds,
      ...responses.map((response) => response.questionId),
    ])]
    migrated.push({
      id: record.sessionId,
      studentId,
      stageId: record.stageId ?? `legacy-stage:${record.unitId}`,
      unitId: record.unitId,
      layer: (record.layer ?? defaultLayer) as LearningLayer,
      formVersion: record.questionFormVersion ?? 1,
      scoringVersion: record.scoringVersion ?? 1,
      variantIndex: 0,
      questionManifest: manifest,
      responses,
      status: 'completed',
      startedAt: attempts[0]?.completedAt ?? record.completedAt,
      updatedAt: record.completedAt,
      completedAt: record.completedAt,
      invalidation: null,
      integrity: 'legacy-completed',
    })
    knownIds.add(record.sessionId)
  }

  const orphanSessionIds = [...new Set(workspace.attempts.flatMap((attempt) => (
    attempt.evidenceKind === 'summative' && attempt.assessmentSessionId
      ? [attempt.assessmentSessionId]
      : []
  )))]
  for (const sessionId of orphanSessionIds) {
    if (knownIds.has(sessionId)) continue
    const attempts = workspace.attempts.filter((attempt) => attempt.assessmentSessionId === sessionId)
    const responses = legacyResponses(attempts)
    const first = attempts[0]
    const last = attempts[attempts.length - 1]
    migrated.push({
      id: sessionId,
      studentId,
      stageId: first?.stageId ?? `legacy-stage:${first?.unitId ?? 'unknown'}`,
      unitId: first?.unitId ?? 'unknown',
      layer: (first?.layer ?? defaultLayer) as LearningLayer,
      formVersion: 1,
      scoringVersion: 1,
      variantIndex: 0,
      questionManifest: responses.map((response) => response.questionId),
      responses,
      status: 'legacy-unverified',
      startedAt: first?.completedAt ?? new Date(0).toISOString(),
      updatedAt: last?.completedAt ?? first?.completedAt ?? new Date(0).toISOString(),
      completedAt: null,
      invalidation: null,
      integrity: 'legacy-unverified',
    })
    knownIds.add(sessionId)
  }
  return migrated
}

export function restoreLegacyLearningState(
  repository: KeyValueRepository = new LocalKeyValueRepository(),
): LegacyLearningState {
  try {
    const raw = repository.get(LEGACY_LEARNING_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as { state?: LegacyLearningState }
    return parsed.state ?? {}
  } catch {
    return {}
  }
}

export function migratePersistedState(
  persistedState: unknown,
  version: number,
  restoredLearningState: LegacyLearningState,
): AppState {
  const state = persistedState as Partial<AppState>
  if (version >= 15 && state.studentIndex && state.workspaces) {
    const normalized = normalizePersistedState(state) as AppState
    const stageScoped = version < 18 ? migrateStageScopedTaskProgress(normalized) : normalized
    return version < 19 ? migrateFormativeQuestionEvidence(stageScoped) : stageScoped
  }

  const profile = state.profile ?? null
  let studentIndex = state.studentIndex ?? []
  let activeStudentId = state.activeStudentId ?? null

  if (studentIndex.length === 0 && profile) {
    const id = profile.identity.id || 'local-demo-student'
    const now = new Date().toISOString()
    activeStudentId = id
    studentIndex = [{
      id,
      name: profile.identity.name,
      grade: profile.identity.grade,
      avatar: profile.identity.avatar,
      hasCompletedPretest: true,
      createdAt: now,
      updatedAt: now,
    }]
  }

  const legacyWorkspaces = (state.workspaces ?? {}) as Record<string, Partial<StudentWorkspace>>
  const workspaces: Record<string, StudentWorkspace> = {}
  for (const account of studentIndex) {
    const base = createEmptyWorkspace(account)
    const legacy = legacyWorkspaces[account.id] ?? {}
    const activeLegacy = account.id === activeStudentId ? {
      profile: migrateStudentLearningProfile(state.profile ?? legacy.profile) ?? null,
      student: state.student ?? base.student,
      currentGrade: state.currentGrade ?? account.grade,
      pretestScore: state.pretestScore ?? null,
      pretestLayer: state.pretestLayer ?? null,
      learningStyle: state.learningStyle ?? null,
      learnerType: state.learnerType ?? legacy.learnerType ?? null,
      weeklyPlanActive: state.weeklyPlanActive ?? legacy.weeklyPlanActive ?? false,
      weeklyPlanStartDate: state.weeklyPlanStartDate ?? legacy.weeklyPlanStartDate ?? null,
      weeklyTaskDone: state.weeklyTaskDone ?? legacy.weeklyTaskDone ?? {},
      taskCompletedAt: state.taskCompletedAt ?? legacy.taskCompletedAt ?? {},
      currentPlan: state.currentPlan ?? restoredLearningState.currentPlan ?? null,
      currentPlanKey: state.currentPlanKey ?? restoredLearningState.currentPlanKey ?? null,
      formativeTaskSessions: state.formativeTaskSessions ?? legacy.formativeTaskSessions ?? [],
      attempts: state.attempts ?? legacy.attempts ?? restoredLearningState.attempts ?? [],
      currentReport: state.currentReport ?? legacy.currentReport ?? restoredLearningState.currentReport ?? null,
      learningEvents: state.learningEvents ?? legacy.learningEvents ?? [],
      archiveStats: { ...createEmptyArchiveStats(), ...(state.archiveStats ?? legacy.archiveStats ?? {}) },
      completedTasks: state.completedTasks ?? {},
      stars: state.stars ?? 0,
      streak: state.streak ?? 1,
      manualLayerOverride: state.manualLayerOverride ?? null,
      dynamicLayer: state.dynamicLayer ?? null,
      currentStage: state.currentStage ?? legacy.currentStage ?? null,
      wrongQuestions: state.wrongQuestions ?? legacy.wrongQuestions ?? [],
      assessmentSessions: state.assessmentSessions ?? legacy.assessmentSessions ?? [],
      assessmentRecords: state.assessmentRecords ?? legacy.assessmentRecords ?? [],
      rewardClaims: state.rewardClaims ?? legacy.rewardClaims ?? [],
      initialProfileSessions: state.initialProfileSessions ?? legacy.initialProfileSessions ?? [],
      vocabMastery: state.vocabMastery ?? {},
      game: state.game ?? createInitialGameState(),
      lastLayerChangeReason: state.lastLayerChangeReason ?? null,
    } : {}
    const merged = { ...base, ...legacy, ...activeLegacy } as StudentWorkspace
    merged.taskCompletedAt = legacy.taskCompletedAt ?? activeLegacy.taskCompletedAt ?? {}
    if (!merged.weeklyPlanStartDate) {
      merged.weeklyPlanStartDate = [...merged.attempts]
        .filter((attempt) => Number.isFinite(new Date(attempt.completedAt).getTime()))
        .sort((left, right) => new Date(left.completedAt).getTime() - new Date(right.completedAt).getTime())[0]?.completedAt ?? null
    }
    if (merged.weeklyPlanStartDate) merged.weeklyPlanActive = true
    merged.profile = migrateStudentLearningProfile(merged.profile) ?? null
    merged.assessmentSessions = migrateLegacyAssessmentSessions(merged, account.id)
    if (!merged.currentStage) {
      const baselineLayer = merged.profile?.recommendedLevel ?? merged.pretestLayer ?? merged.student.layer
      if (baselineLayer) merged.currentStage = createLearningStage({ layer: baselineLayer, source: 'pretest' })
    }
    if (version < 11 && merged.profile && merged.ownedOutfits.length === 0) {
      const learnerType = merged.learnerType ?? getPreferredLearnerType(merged.profile.preference)
      const starterOutfit = getStarterOutfit(learnerType)
      if (starterOutfit !== 'none') {
        merged.currentOutfit = starterOutfit
        merged.unlockedOutfits = [starterOutfit]
        merged.ownedOutfits = [starterOutfit]
      }
    }
    workspaces[account.id] = merged
  }

  const activeWorkspace = activeStudentId ? workspaces[activeStudentId] : undefined
  const migrated = {
    ...state,
    activeStudentId,
    studentIndex,
    workspaces,
    ...(activeWorkspace ? activeFieldsFromWorkspace(activeWorkspace) : {}),
  } as AppState
  return migrateFormativeQuestionEvidence(migrateStageScopedTaskProgress(migrated))
}
